import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useShortlist } from "../lib/useShortlist";
import { useVolunteers } from "../lib/useVolunteers";

export default function Shortlist() {
  const { ids, has, toggle } = useShortlist();
  const { data: volunteers, loading, error } = useVolunteers();
  const [filter, setFilter] = useState("");

  const ministries = useMemo(() => {
    const set = new Set<string>();
    for (const v of volunteers) for (const m of v.ministries) set.add(m);
    return Array.from(set).sort();
  }, [volunteers]);

  const [ministryFilter, setMinistryFilter] = useState<string>("");

  const visible = useMemo(() => {
    return volunteers
      .filter((v) =>
        filter
          ? `${v.firstName} ${v.lastName}`.toLowerCase().includes(filter.toLowerCase())
          : true,
      )
      .filter((v) => (ministryFilter ? v.ministries.includes(ministryFilter) : true))
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }, [volunteers, filter, ministryFilter]);

  return (
    <main className="page page--shortlist">
      <p className="back-link">
        <Link to="/">← Back</Link>
      </p>

      <header className="shortlist-page__header">
        <h1>Shortlist</h1>
        <p className="shortlist-page__lede">
          Star volunteers you'd reach out to first when a sub-request goes past
          the 48-hour mark. Curated entirely by you — no judgments from the system.
        </p>
        {ids.length === 0 ? (
          <p className="shortlist-page__empty">
            Tap any volunteer to add them to your shortlist for last-minute
            coverage.
          </p>
        ) : (
          <p className="shortlist-page__meta">
            {ids.length} {ids.length === 1 ? "volunteer" : "volunteers"} on your shortlist.
          </p>
        )}
      </header>

      <div className="shortlist-page__filters">
        <input
          className="shortlist-page__search"
          placeholder="Search by name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select
          className="shortlist-page__ministry"
          value={ministryFilter}
          onChange={(e) => setMinistryFilter(e.target.value)}
        >
          <option value="">All ministries</option>
          {ministries.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="placeholder">Loading…</p>
      ) : error ? (
        <p className="placeholder">Couldn't load: {error}</p>
      ) : (
        <ul className="shortlist-page__list">
          {visible.map((v) => {
            const starred = has(v.id);
            return (
              <li key={v.id} className="shortlist-page__row">
                <button
                  type="button"
                  className={`shortlist-page__star ${starred ? "is-on" : ""}`}
                  aria-pressed={starred}
                  aria-label={starred ? `Remove ${v.firstName} from shortlist` : `Add ${v.firstName} to shortlist`}
                  onClick={() => toggle(v.id)}
                >
                  {starred ? "★" : "☆"}
                </button>
                <div className="shortlist-page__row-text">
                  <div className="shortlist-page__name">
                    {v.firstName} {v.lastName}
                  </div>
                  <div className="shortlist-page__contact">
                    {/* Separator only between ministries and phone when
                        both sides have content — an empty ministry list
                        must not leave an orphan leading " · ". */}
                    {[v.ministries.join(", "), v.phone || "no cell"]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
